import React, { useMemo, useState } from "react";
import { Files, Tray, DeviceMobile, Star, Highlighter, FolderSimple, FolderOpen, CaretRight, CaretDown, Tag, Eye } from "@phosphor-icons/react";
import { KAT_LABEL, statusOf } from "./dmsUtils";

const QUICK = [
  { key: "alle", label: "Alle Dokumente", icon: Files, test: () => true },
  { key: "posteingang", label: "Posteingang (ohne Akte)", icon: Tray, test: (d) => !d.akte },
  { key: "neu", label: "Zu prüfen", icon: Eye, test: (d) => statusOf(d) === "neu" },
  { key: "mandant", label: "Vom Mandanten", icon: DeviceMobile, test: (d) => d.source === "portal" },
  { key: "favoriten", label: "Favoriten", icon: Star, test: (d) => !!d.favorit },
  { key: "markiert", label: "Mit Markierungen", icon: Highlighter, test: (d) => (d.annotationen || 0) > 0 },
];

export const matchesView = (d, sel) => {
  if (sel.view === "kategorie") return d.kategorie === sel.kat;
  if (sel.view === "tag") return (d.tags || []).includes(sel.tag);
  if (sel.view === "akte") return d.akte_id === sel.akteId && (!sel.kat || d.kategorie === sel.kat);
  return (QUICK.find(q => q.key === sel.view) || QUICK[0]).test(d);
};

export function viewTitle(sel, akten) {
  if (sel.view === "kategorie") return KAT_LABEL[sel.kat] || sel.kat;
  if (sel.view === "tag") return `#${sel.tag}`;
  if (sel.view === "akte") {
    const a = akten.find(x => x.id === sel.akteId);
    return `${a?.aktenzeichen || "Akte"}${a?.mandant_name ? ` · ${a.mandant_name}` : ""}${sel.kat ? ` › ${KAT_LABEL[sel.kat] || sel.kat}` : ""}`;
  }
  return QUICK.find(q => q.key === sel.view)?.label || "Dokumente";
}

export default function DmsTree({ docs, akten, tags, sel, onSelect, testId = "dms-tree" }) {
  const [open, setOpen] = useState({});
  const [aktenOpen, setAktenOpen] = useState(true);
  const [katOpen, setKatOpen] = useState(true);
  const [tagOpen, setTagOpen] = useState(false);

  const counts = useMemo(() => {
    const quick = Object.fromEntries(QUICK.map(q => [q.key, docs.filter(q.test).length]));
    const kat = {}; const akte = {}; const akteKat = {};
    for (const d of docs) {
      kat[d.kategorie] = (kat[d.kategorie] || 0) + 1;
      if (d.akte_id) {
        akte[d.akte_id] = (akte[d.akte_id] || 0) + 1;
        akteKat[d.akte_id] = akteKat[d.akte_id] || {};
        akteKat[d.akte_id][d.kategorie] = (akteKat[d.akte_id][d.kategorie] || 0) + 1;
      }
    }
    return { quick, kat, akte, akteKat };
  }, [docs]);

  const aktenMitDocs = useMemo(() => akten.filter(a => counts.akte[a.id]).sort((a, b) => (b.aktenzeichen || "").localeCompare(a.aktenzeichen || "")), [akten, counts]);
  const is = (v) => sel.view === v.view && (sel.kat || null) === (v.kat || null) && (sel.akteId || null) === (v.akteId || null) && (sel.tag || null) === (v.tag || null);

  const Row = ({ active, depth = 0, icon: Icon, label, count, onClick, chevron, testId, color }) => (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`w-full flex items-center gap-2 rounded-md text-left text-[13px] transition-colors ${active ? "bg-[#1A2238] text-white" : "text-[#374151] hover:bg-[#F3F4F6]"}`}
      style={{ paddingLeft: 8 + depth * 14, paddingRight: 8, height: 32 }}>
      {chevron !== undefined && <span className={`shrink-0 ${active ? "text-white/70" : "text-[#9CA3AF]"}`}>{chevron ? <CaretDown size={12} /> : <CaretRight size={12} />}</span>}
      {Icon && <Icon size={16} weight={active ? "fill" : "duotone"} className={`shrink-0 ${active ? "" : color || "text-[#6B7280]"}`} />}
      <span className="truncate flex-1">{label}</span>
      {count !== undefined && <span className={`text-[11px] font-mono ${active ? "text-white/70" : "text-[#9CA3AF]"}`}>{count}</span>}
    </button>
  );

  return (
    <nav className="h-full overflow-y-auto p-2 space-y-4 text-sm" data-testid={testId}>
      <div className="space-y-0.5">
        {QUICK.map(q => <Row key={q.key} active={is({ view: q.key })} icon={q.icon} label={q.label} count={counts.quick[q.key]} onClick={() => onSelect({ view: q.key })} testId={`tree-${q.key}`} color={q.key === "favoriten" ? "text-[#F59E0B]" : undefined} />)}
      </div>

      <div>
        <Row icon={aktenOpen ? FolderOpen : FolderSimple} label="Akten" chevron={aktenOpen} onClick={() => setAktenOpen(v => !v)} testId="tree-akten-toggle" count={aktenMitDocs.length} />
        {aktenOpen && (
          <div className="space-y-0.5 mt-0.5">
            {aktenMitDocs.length === 0 && <p className="text-xs text-[#9CA3AF] pl-6 py-1">Noch keine Akte mit Dokumenten.</p>}
            {aktenMitDocs.map(a => {
              const isOpen = !!open[a.id];
              const active = is({ view: "akte", akteId: a.id });
              return (
                <div key={a.id}>
                  <Row depth={1} active={active} icon={isOpen ? FolderOpen : FolderSimple} label={`${a.aktenzeichen}${a.mandant_name ? ` · ${a.mandant_name}` : ""}`} count={counts.akte[a.id]}
                    chevron={isOpen} onClick={() => { setOpen(o => ({ ...o, [a.id]: active ? !isOpen : true })); onSelect({ view: "akte", akteId: a.id }); }} testId={`tree-akte-${a.id}`} color="text-[#1A2238]" />
                  {isOpen && Object.entries(counts.akteKat[a.id] || {}).sort().map(([k, n]) => (
                    <Row key={k} depth={2} active={is({ view: "akte", akteId: a.id, kat: k })} icon={FolderSimple} label={KAT_LABEL[k] || k} count={n} onClick={() => onSelect({ view: "akte", akteId: a.id, kat: k })} testId={`tree-akte-${a.id}-${k}`} />
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <Row icon={FolderSimple} label="Kategorien" chevron={katOpen} onClick={() => setKatOpen(v => !v)} testId="tree-kategorien-toggle" />
        {katOpen && (
          <div className="space-y-0.5 mt-0.5">
            {Object.entries(KAT_LABEL).filter(([k]) => counts.kat[k]).map(([k, l]) => (
              <Row key={k} depth={1} active={is({ view: "kategorie", kat: k })} icon={FolderSimple} label={l} count={counts.kat[k]} onClick={() => onSelect({ view: "kategorie", kat: k })} testId={`tree-kat-${k}`} />
            ))}
          </div>
        )}
      </div>

      {tags.length > 0 && (
        <div>
          <Row icon={Tag} label="Schlagwörter" chevron={tagOpen} onClick={() => setTagOpen(v => !v)} testId="tree-tags-toggle" count={tags.length} />
          {tagOpen && (
            <div className="space-y-0.5 mt-0.5">
              {tags.map(t => <Row key={t.tag} depth={1} active={is({ view: "tag", tag: t.tag })} icon={Tag} label={`#${t.tag}`} count={t.anzahl} onClick={() => onSelect({ view: "tag", tag: t.tag })} testId={`tree-tag-${t.tag}`} />)}
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
